import "./App.css";
import { Route, Routes, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import OneSignal from "react-onesignal";

import Signin from "src/Pages/Authentication/Signin";
import Signup from "src/Pages/Authentication/Signup";
import ForgotPassword from "src/Pages/Authentication/ForgotPassword";
import ResetPassword from "src/Pages/Authentication/ResetPassword";
// import Main from "./Pages/main";
import WO from "./Pages/WO/WO";
import PO from "./Pages/PO/PO";
import { useEffect, useRef, useState } from "react";
import Customer from "./Pages/Customer/Customer";
import Calendar from "./Pages/Calendar/Calendar";
import WODetail from "./Pages/WO/details/WoDetail";
import PODetail from "./Pages/PO/PoDetail";
import ServiceTicket from "./Pages/ServiceTicket/UpdatedServiceTicket";
import TicketLayout from "./Pages/ticket/TicketLayout";
import TicketDetail from "./Pages/ticket/TicketDetail";
import UserGuides from "./Pages/userguides/UserGuides";
import NotificationSettings from "./Components/NotificationDrawer/NotificationSettings";
import NotificationsPage from "./Components/NotificationDrawer/NotificationsPage";
import UserService from "./Services/API/user.service";
import PermissionService from "./Services/API/permission.service";
import { getActingTenant } from "./Common/tenant";
import { loadVocabulary } from "./Common/vocab";
import Recommendations from "./Pages/Recommendations/Recommendations";
import TimeOff from "./Pages/TimeOff/TimeOff";
import MyTimeOff from "./Pages/mytimeoff/MyTimeOff";
import TOS from "./Pages/TOS/tos";
import Privacy from "./Pages/TOS/privacy";
import SetPassword from "./Pages/Authentication/SetPassword/SetPassword";
import LayoutWithTopBar from "./Layout/layoutwithtopbar";
import Account from "./Pages/Setting/Account/Account";
import Team from "./Pages/Setting/Team/team";
import Roles from "./Pages/Setting/IAM/Roles";
import RoleDetail from "./Pages/Setting/IAM/RoleDetail";
import Policies from "./Pages/Setting/IAM/Policies";
import PolicyDetail from "./Pages/Setting/IAM/PolicyDetail";
import NotificationCenter from "./Pages/Setting/Notification/notificationcenter";
import Company from "./Pages/Setting/Company/company";
import Vendor from "./Pages/Setting/Vendor/vendor";
import ReportCenter from "./Pages/Setting/Report/reportcenter";
import SettingsLayout from "./Layout/settingslayout";
import Onboarding from "./Pages/Onboarding/Onboarding";
import Branch from "./Pages/Setting/Branch/branch";
import LocationDetail from "./Pages/Customer/LocationDetail";
import CustomerDetail from "./Pages/Customer/CustomerDetail";
import Materials from "./Pages/Setting/material/Materials";
import Services from "./Pages/Setting/services/Services";
import CompanyManagement from "./Pages/CompanyManagement/CompanyManagement";
import AppVersions from "./Pages/AppVersions/AppVersions";
import WoAttachments from "./Pages/WO/WoAttachments";
import { useLocation } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "src/Redux/store";
import FormBuilderComponent from "./Pages/WO/Components/forms/FormBuilderComponent";
import FormViewerComponent from "./Pages/WO/Components/forms/FormViewerComponent";
import TemplateList from "./Pages/forms/templates/TemplateList";
import AllForms from "./Pages/forms/allforms/AllForms";
import RequirePhoneModal from "./Components/Authentication/RequirePhoneModal";
import RequirePermission from "./Components/RequirePermission";
import NotFound from "./Pages/NotFound";
import VersionCheck from "./Components/VersionCheck/VersionCheck";

// Where a Super Admin was inside the Platform view; survives the full page
// reload that typing a URL causes.
const PLATFORM_LAST_PATH_KEY = "platformLastPath";

function App() {
  const isAuthenticated = useSelector((state) => state.Auth.isAuthenticated);
  const woNumber = useSelector((state) => state.Header.woNumber);
  const poNumber = useSelector((state) => state.Header.poNumber);
  const PrivateRoute = ({ auth: isAuthenticated, children }) => {
    const location = useLocation();
    if (!isAuthenticated) return <Navigate to="/" />;

    // A Super Admin who hasn't entered a tenant is confined to the Platform
    // view: tenant pages hold no data for them, so URL manipulation must not
    // escape it. Entering a tenant (or logging in as anyone else) lifts this.
    const user = UserService.getCurrentUser();
    const isSuperAdmin = user?.roles?.[0]?.name === "Super Admin";
    const inPlatformMode = isSuperAdmin && !getActingTenant();
    if (inPlatformMode) {
      const path = location.pathname;
      if (path.startsWith("/platform")) {
        // Remember the platform page so a blocked navigation bounces back here.
        // sessionStorage, not a ref: typing a URL reloads the app, which would
        // reset in-memory state.
        sessionStorage.setItem(PLATFORM_LAST_PATH_KEY, path);
      } else {
        // Ticket Management is a platform page: keep the sub-path so
        // /ticket/detail/5 lands on /platform/ticket/detail/5.
        if (path.startsWith("/ticket")) {
          return <Navigate to={`/platform${path}`} replace />;
        }
        const back = sessionStorage.getItem(PLATFORM_LAST_PATH_KEY) || "/platform/tenants";
        return <Navigate to={back} replace />;
      }
    }

    return children;
  };
  // function PrivateRoute({ auth: isAuthenticated, children }) {
  //   return isAuthenticated ? children : <Navigate to="/" />;
  // }

  // try {
  //   OneSignal.init({
  //     appId: "6421c1e6-f5ed-4b26-8df7-dca0d80bf80f",
  //   }).then(() => {
  // OneSignal.Debug.setLogLevel("warn");
  //     OneSignal.login("rodrigo");
  //     OneSignal.User.addAlias("myAlias", "1");
  //     console.log("subscription ID: ", OneSignal.User.PushSubscription.id);
  //   });
  // } catch (e) {
  //   console.log(e);
  // }

  // Tracks whether OneSignal.init() has finished. login()/logout() called
  // before init completes are silently dropped by the web SDK, so we must
  // wait for this before syncing the authenticated user.
  const oneSignalReadyRef = useRef(false);

  // Links the current browser's push subscription to the logged-in user's
  // email (external_id). The backend targets users by external_id, so without
  // this the subscription exists but no notification can ever reach the user.
  const syncOneSignalUser = async () => {
    if (!oneSignalReadyRef.current) return;
    try {
      if (isAuthenticated) {
        const user = UserService.getCurrentUser();
        if (user?.email) {
          await OneSignal.login(user.email);
          // Covers the "subscribed before login" case: a subscription may
          // already exist but be opted out / unattached for this user.
          if (OneSignal.User?.PushSubscription?.optedIn === false) {
            await OneSignal.User.PushSubscription.optIn();
          }
          console.log("OneSignal linked: ", OneSignal.User.PushSubscription.id, user.email);
        }
      } else {
        await OneSignal.logout();
      }
    } catch (error) {
      console.log("OneSignal sync error: ", error);
    }
  };

  useEffect(() => {
    syncOneSignalUser();
  }, [isAuthenticated]);

  // Keep the cached IAM permissions fresh for authenticated sessions (covers
  // page reloads and sessions that predate the login-time fetch).
  // `can()` reads localStorage synchronously during render, so the first render
  // after a reload still uses the stale cache; when the fetched rules differ,
  // bump state to re-render the tree with the fresh permissions (otherwise a
  // second manual refresh would be needed to see newly assigned permissions).
  const [, setPermissionsVersion] = useState(0);
  // Entering or leaving a tenant swaps which company the API answers for.
  const actingTenantId = getActingTenant()?.identifier || "";
  useEffect(() => {
    if (isAuthenticated) {
      const before = localStorage.getItem("permissions");
      PermissionService.loadAndStore()
        .then(() => {
          if (localStorage.getItem("permissions") !== before) {
            setPermissionsVersion((v) => v + 1);
          }
        })
        .catch((e) => console.log("failed to refresh permissions: ", e));

      // The stored user goes stale the same way: role/branch changes made
      // after login live only in the login-time localStorage copy, which the
      // role helpers (isAdmin()/isSuperAdmin()/...) read synchronously.
      // Re-fetch the own profile (auth-only /me) and re-render if it changed.
      const beforeUser = localStorage.getItem("user");
      UserService.getMe()
        .then((res) => {
          if (!res?.data) return;
          UserService.saveCurrentUser(res.data);
          if (localStorage.getItem("user") !== beforeUser) {
            setPermissionsVersion((v) => v + 1);
          }
        })
        .catch((e) => console.log("failed to refresh current user: ", e));

      // The company's wording goes stale the same way, and a Super Admin who
      // enters a tenant needs that tenant's words, not their own.
      const beforeVocab = localStorage.getItem("vocabulary");
      loadVocabulary().then(() => {
        if (localStorage.getItem("vocabulary") !== beforeVocab) {
          setPermissionsVersion((v) => v + 1);
        }
      });
    }
  }, [isAuthenticated, actingTenantId]);

  useEffect(() => {
    // console.log("Notification.permission: ", Notification.permission);
    // if (Notification.permission !== "granted") {
    //   Notification.requestPermission().then((permission) => {
    //     console.log("Permission: ", permission);
    //   });
    // }
    let detachListeners;
    const setupOneSignal = async () => {
      detachListeners = await runOneSignal();
      oneSignalReadyRef.current = true;
      // init() may finish after the auth effect already ran; sync now so a
      // user who was already authenticated on page load still gets linked.
      syncOneSignalUser();
    };
    setupOneSignal();
    return () => {
      if (typeof detachListeners === "function") {
        detachListeners();
      }
    };
  }, []);

  async function runOneSignal() {
    const appID =
      process.env.REACT_APP_ENV === "production"
        ? "7b554f0c-68e1-4701-97cb-9d46554611e7"
        : process.env.REACT_APP_ENV === "staging"
          ? "78cf09be-f363-4294-838d-695eb71bdee6"
          : process.env.REACT_APP_ENV === "devai"
            ? "99bfab43-8336-42ce-84a2-6cdb67257389"
            : "6421c1e6-f5ed-4b26-8df7-dca0d80bf80f";
    // OneSignal.Debug.setLogLevel("trace");
    await OneSignal.init({ appId: appID });
    OneSignal.User.PushSubscription.addEventListener("change", pushSubscriptionChangeListener);

    const handleForegroundWillDisplay = async (event) => {
      console.log("OneSignal: notification foregroundWillDisplay:", event);
    };
    const handleNotificationClick = async (event) => {
      console.log("OneSignal: notification click:", event);
      const additionalData = event?.notification?.additionalData;
      if (additionalData?.type === "TICKET_MESSAGE" && additionalData?.ticketId) {
        const targetPath = `/ticket/detail/${additionalData.ticketId}`;
        if (window.location.pathname !== targetPath) {
          window.location.assign(targetPath);
        }
      }
    };

    OneSignal.Notifications.addEventListener("foregroundWillDisplay", handleForegroundWillDisplay);
    OneSignal.Notifications.addEventListener("click", handleNotificationClick);

    return () => {
      OneSignal.User.PushSubscription.removeEventListener("change", pushSubscriptionChangeListener);
      OneSignal.Notifications.removeEventListener("foregroundWillDisplay", handleForegroundWillDisplay);
      OneSignal.Notifications.removeEventListener("click", handleNotificationClick);
    };
  }

  function pushSubscriptionChangeListener(event) {
    if (event.current.token) {
      console.log("The push subscription has received a token! ", OneSignal.User.PushSubscription.id);
      const user = UserService.getCurrentUser();
      // OneSignal.User.addAlias("Email", user.email);
      if (user) {
        OneSignal.login(user.email);
        console.log(
          "The push subscription has received a token! subscription_id: ",
          OneSignal.User.PushSubscription.id,
          user.email,
        );
      }
    }
  }

  // const isSupported = OneSignal.Notifications.isPushSupported();
  // OneSignal.Slidedown.promptPush();
  // console.log("subscription ID: ", OneSignal.User.PushSubscription.id);
  // OneSignal.login("rodeli6607@armablog.com");

  // OneSignal.Notifications.addEventListener("click", (event) => {
  //   console.log("OneSignal: notification clicked:", event);
  // });

  // OneSignal.Notifications.addEventListener("foregroundWillDisplay", (event) => {
  //   console.log("OneSignal: notification foregroundWillDisplay:", event);
  // });

  // OneSignal.Slidedown.promptPush();

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ToastContainer />
        <VersionCheck />
        {isAuthenticated && <RequirePhoneModal />}
        <Routes>
          <Route path="/" element={<Signin />} />
          {/* <Route path="/signup" element={<Signup />} /> */}
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/resetpassword/:token" element={<ResetPassword />} />
          <Route path="/invite" element={<SetPassword />} />
          {/* New-customer onboarding: full screen, outside the app shell — the
              tenant isn't configured yet, so the side menu has nothing to show. */}
          <Route
            path="/onboarding"
            element={
              <PrivateRoute auth={isAuthenticated}>
                <Onboarding />
              </PrivateRoute>
            }
          />
          <Route path="/form-viewer/:formId" element={<FormViewerComponent />} />
          <Route element={<LayoutWithTopBar />}>
            <Route
              path="/work"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="wo:read">
                    {/* Keyed apart from Jobs (/jobs, same component): without
                        it React reuses the page when switching between them,
                        the branch bar never sets ?branch and the list stays
                        empty until a refresh. */}
                    <WO key="work" />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            {/* Jobs: the work orders the user is assigned to, as the tech
                app's Jobs tab. */}
            <Route
              path="/jobs"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="wo:read">
                    <WO key="jobs" jobs />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/work/:id"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="wo:read">
                    <WODetail />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/work/:id/attachments"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="wo:readAttachment">
                    <WoAttachments />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="po:read">
                    <PO />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase/:id"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="po:read">
                    <PODetail />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/recommendations"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="recommendation:read">
                    <Recommendations />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/customer"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="customer:read">
                    <Customer />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route path="/calendar" element={<Navigate to="/calendar/wo" replace />} />
            <Route
              path="/calendar/:tab"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <Calendar />
                </PrivateRoute>
              }
            />
            <Route path="/time-off" element={<Navigate to="/time-off/all" replace />} />
            <Route
              path="/time-off/:tab"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="time-off:manage">
                    <TimeOff />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route path="/my-time-off" element={<Navigate to="/my-time-off/all" replace />} />
            <Route
              path="/my-time-off/:tab"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <MyTimeOff />
                </PrivateRoute>
              }
            />
            <Route
              path="/userguides"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <UserGuides />
                </PrivateRoute>
              }
            />
            {/* The full inbox behind the drawer's "View all notifications", and
                the user's own preferences behind its gear. No permission gate on
                either: every signed-in user has them, and the backend scopes
                every row to the caller. */}
            <Route
              path="/notifications"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <NotificationsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/notification-settings"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <NotificationSettings />
                </PrivateRoute>
              }
            />
            {/* Ticket pages stay open to all authenticated users: everyone can
                file a bug report ("Report Bug") and view/comment on tickets.
                ticket:manage gates only the staff management actions (status
                update / delete) inside TicketDetail, and the backend enforces it. */}
            <Route
              path="/ticket"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <TicketLayout />
                </PrivateRoute>
              }
            />
            <Route
              path="/ticket/new"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <TicketLayout />
                </PrivateRoute>
              }
            />
            <Route
              path="/ticket/detail/:ticketId"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <TicketDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/customer/:accountId"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="customer:read">
                    <CustomerDetail />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/location/:locationId"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="customer:read">
                    <LocationDetail />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/:woId/serviceticket"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="wo:read">
                    <ServiceTicket />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/form-templates"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="form:templateRead">
                    <TemplateList />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/all-forms"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="form:read">
                    <AllForms />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/form-builder"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <FormBuilderComponent />
                </PrivateRoute>
              }
            />
            <Route
              path="/form-builder/:woId"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <FormBuilderComponent />
                </PrivateRoute>
              }
            />

            <Route
              path="/report/:tab"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission anyOf={["report:adHocRead", "report:scheduledRead", "ai:ask"]}>
                    <ReportCenter />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route path="/report" element={<Navigate to="/report/ad-hoc" replace />} />
            {/* Legacy settings-tab path — Report Center now lives at /report/:tab. */}
            <Route path="/settings/report" element={<Navigate to="/report/ad-hoc" replace />} />

            <Route path="/team" element={<Navigate to="/team/manage" replace />} />
            <Route
              path="/team/:tab"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="user:read">
                    <Team />
                  </RequirePermission>
                </PrivateRoute>
              }
            />

            {/* IAM — Roles & Policies */}
            <Route
              path="/roles"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="iam:readRole">
                    <Roles />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/roles/:id"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="iam:readRole">
                    <RoleDetail />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/policies"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="iam:readPolicy">
                    <Policies />
                  </RequirePermission>
                </PrivateRoute>
              }
            />
            <Route
              path="/policies/:id"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <RequirePermission permission="iam:readPolicy">
                    <PolicyDetail />
                  </RequirePermission>
                </PrivateRoute>
              }
            />

            <Route
              path="/platform/tenants"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <CompanyManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/platform/app-versions"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <AppVersions />
                </PrivateRoute>
              }
            />
            {/* Ticket Management lives under /platform for a Super Admin; the
                bare /ticket routes stay for everyone else. */}
            <Route
              path="/platform/ticket"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <TicketLayout />
                </PrivateRoute>
              }
            />
            <Route
              path="/platform/ticket/detail/:ticketId"
              element={
                <PrivateRoute auth={isAuthenticated}>
                  <TicketDetail />
                </PrivateRoute>
              }
            />
            {/* Legacy path for the tenants page. */}
            <Route path="/companies" element={<Navigate to="/platform/tenants" replace />} />

            <Route element={<SettingsLayout />}>
              <Route
                path="/settings/account"
                element={
                  <PrivateRoute auth={isAuthenticated}>
                    <Account />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/notification"
                element={
                  <PrivateRoute auth={isAuthenticated}>
                    <RequirePermission permission="notification:read">
                      <NotificationCenter />
                    </RequirePermission>
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/company"
                element={
                  <PrivateRoute auth={isAuthenticated}>
                    <RequirePermission permission="company:read">
                      <Company />
                    </RequirePermission>
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/vendor"
                element={
                  <PrivateRoute auth={isAuthenticated}>
                    <RequirePermission permission="vendor:read">
                      <Vendor />
                    </RequirePermission>
                  </PrivateRoute>
                }
              />

              <Route
                path="/settings/branch"
                element={
                  <PrivateRoute auth={isAuthenticated}>
                    <RequirePermission permission="branch:read">
                      <Branch />
                    </RequirePermission>
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/materials"
                element={
                  <PrivateRoute auth={isAuthenticated}>
                    <RequirePermission permission="material:read">
                      <Materials />
                    </RequirePermission>
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/services"
                element={
                  <PrivateRoute auth={isAuthenticated}>
                    <RequirePermission permission="service:read">
                      <Services />
                    </RequirePermission>
                  </PrivateRoute>
                }
              />
            </Route>
          </Route>
          <Route path="/terms" element={<TOS />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </PersistGate>
    </Provider>
  );
}

export default App;
